<template>
  <div
    class="c-pdf-viewer"
    :class="{ '-loading': loading }"
  >
    <c-loader v-if="loading" class="loader" />

    <div v-show="!loading" ref="container" class="viewer-container">
      <resize-sensor initial @resize="resizeScale" />
    </div>
  </div>
</template>

<script>
import { defineAsyncComponent, markRaw } from 'vue'
import ResizeSensor from 'vue-resize-sensor'

import { AnnotationMode } from 'pdfjs-dist/legacy/build/pdf.mjs'

import {
  PDFPageView,
  SimpleLinkService,
  EventBus
} from 'pdfjs-dist/legacy/web/pdf_viewer.mjs'

import {
  TEXT_LAYER_MODE,
  scaleValidator,
  isPDFDocumentLoadingTask,
  createLoadingTask,
  initWorker,
} from './helpers'

export default {
  name: 'CPdfViewer',

  components: {
    ResizeSensor,
    CLoader: defineAsyncComponent(() => import('@convenia/components/CLoader'))
  },

  props: {
    src: {
      type: [ String, Function ],
      required: true
    },
    page: {
      type: Number,
      default: 1
    },
    annotations: {
      type: Boolean,
      default: true
    },
    text: {
      type: Boolean,
      default: false
    },
    resize: {
      type: Boolean,
      default: true
    },
    rotate: {
      type: Boolean,
      default: false
    },
    scale: {
      type: String,
      default: 'page-width',
      validator: scaleValidator,
    }
  },

  data () {
    return {
      internalSrc: this.src,
      pdfViewer: null,
      loading: true,
      scaleSet: false
    }
  },

  methods: {
    initViewer () {
      if (!isPDFDocumentLoadingTask(this.internalSrc)) {
        this.internalSrc = createLoadingTask(this.internalSrc)
        this.$emit('loading', true)
      }

      const { container } = this.$refs

      this.internalSrc
        .then((pdfDoc) => {
          this.pdfData = pdfDoc
          this.$emit('meta:num-pages', pdfDoc.numPages)
          return pdfDoc.getPage(this.page)
        })
        .then((pdfPage) => {
          const textLayerMode = this.text
            ? TEXT_LAYER_MODE.ENABLE
            : TEXT_LAYER_MODE.DISABLE

          const annotationMode = this.annotations
            ? AnnotationMode.ENABLE
            : AnnotationMode.DISABLE

          this.pdfViewer = markRaw(new PDFPageView({
            container,
            textLayerMode,
            annotationMode,
            id: this.page,
            scale: 1,
            eventBus: new EventBus(),
            layerProperties: {
              linkService: new SimpleLinkService(),
            },
            defaultViewport: pdfPage.getViewport({ scale: 1 })
          }))

          this.loading = false
          this.$emit('loading', false)

          this.pdfViewer.setPdfPage(pdfPage)
          this.drawScaled(this.scale)
        })
        .catch(err => console.error('initViewer.error: ', err))
    },

    calculateScale () {
      const { width = -1, height = -1 } = this.pdfViewer?.viewport || {}

      if (!this.$refs.container) return

      this.pdfViewer.update({ scale: 1, rotation: this.rotate })

      const calcWidth = width === -1 && height === -1
        ? this.$refs.container.offsetWidth
        : width

      return calcWidth / this.pdfViewer.viewport.width
    },

    calculateScaleHeight () {
      if (!this.$refs.container) return

      this.pdfViewer.update({ scale: 1, rotation: this.rotate })
      const height = this.$refs.container.offsetHeight
      const parentEl = this.$refs.container.parentElement.parentElement

      return parentEl.offsetHeight / height
    },

    drawScaled (newScale) {
      if (!this.pdfViewer || this.scaleSet) return

      let calcNewScale

      if (newScale === 'page-width') {
        calcNewScale = this.calculateScale()
        this.$emit('update:scale', newScale)
      } else if (newScale === 'page-height') {
        calcNewScale = this.calculateScaleHeight()
        this.$emit('update:scale', newScale)
      }

      this.scaleSet = true
      this.pdfViewer.update({ scale: calcNewScale || newScale, rotation: this.rotate })
      this.pdfViewer.draw()
      this.loading = false
      this.$emit('loading', false)
    },

    resizeScale () {
      if (this.resize)
        this.drawScaled('page-width')
    },

    destroyViewer () {
      if (!this.pdfViewer) return

      this.pdfViewer.destroy()
      this.pdfViewer = null
    },
  },

  mounted () {
    this.$nextTick(() => {
      initWorker()
      this.initViewer()
    })
  },

  beforeDestroy () {
    this.destroyViewer()
  },

  beforeUnmount () {
    this.destroyViewer()
  },
}
</script>

<style lang="scss">
.c-pdf-viewer {
  display: flex;
  align-items: center;
  justify-content: center;

  &.-loading {
    width: 524px !important;
    // Why? Honestly, I'm not sure.
    height: calc(100vh - 300px) !important;

    @include xs-mobile { width: 100px !important; }
  }

  & > .loader {
    transform: none !important;
    opacity: 0.3;
  }

  & > .viewer-container {
    height: 100%;
    max-height: 100%;
    min-width: 554px;

    @include xs-mobile { min-width: 200px; }

    & > .page,
    & > .page > .canvasWrapper,
    & > .page > .canvasWrapper canvas {
      height: 100% !important;
      width: 100% !important;
      max-height: 100% !important;
    }

    & > .page > .textLayer,
    & > .page > .annotationLayer {
      display: none;
    }
  }
}
</style>
